/**
 * EQUIPMENT SELECTION STATE — which equipment envelope (sourced catalog or
 * user-supplied) currently supplies the scenario's working duty, and the ONE
 * sanctioned way to resize that fleet by block count.
 *
 * Solver-integrity rules:
 *  - `resizeGenerationEquipmentFleet` never hand-builds units. It re-runs the
 *    existing apply functions, so the catalog `appliedDutyFingerprint` is
 *    recomputed by the one write path and server-side
 *    `validateGenerationEquipmentBasis` stays green.
 *  - The user-entry selection is VERIFIED on read: the flag alone is never
 *    trusted — the current units must still match a re-application of the
 *    entry, otherwise hand edits would be silently reverted by a resize.
 */
import type {
  GenerationImpactScenarioInput,
  GenerationUnit,
} from '../../types/generationImpact';
import { applyGenerationEquipmentEnvelope } from './generationEquipmentCatalog';
import {
  applyGenerationUserEquipmentEnvelope,
  validateGenerationUserEquipmentEntry,
} from './userEquipmentEnvelope';
import { rescaleGenerationProjectDutyBasis } from './generationProjectDuty';

export type ActiveGenerationEquipmentSelection =
  | { kind: 'catalog'; catalogId: string; manufacturer: string; model: string }
  | { kind: 'user'; entryId: string; manufacturer: string; model: string };

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(1, Math.abs(left), Math.abs(right)) * 1e-9;
}

function unitsMatchAppliedDuty(
  actual: readonly GenerationUnit[],
  expected: readonly GenerationUnit[],
): boolean {
  return actual.length === expected.length && actual.every((unit, index) => {
    const reference = expected[index]!;
    return unit.count === reference.count
      && unit.technology === reference.technology
      && nearlyEqual(unit.isoGrossMWPerUnit, reference.isoGrossMWPerUnit)
      && nearlyEqual(unit.ambientFactor, reference.ambientFactor)
      && nearlyEqual(unit.degradationFactor, reference.degradationFactor)
      && nearlyEqual(unit.auxiliaryLoadMWPerUnit, reference.auxiliaryLoadMWPerUnit)
      && nearlyEqual(unit.heatRateBtuPerKWhHhv, reference.heatRateBtuPerKWhHhv)
      && (unit.forcedOutageRate ?? null) === (reference.forcedOutageRate ?? null);
  });
}

/**
 * The equipment selection whose duty the scenario currently carries, or null
 * when the fleet is a generic planning configuration (or has been hand-edited
 * away from the applied user entry).
 */
export function activeGenerationEquipmentSelection(
  input: GenerationImpactScenarioInput,
): ActiveGenerationEquipmentSelection | null {
  if (input.equipmentBasis) {
    return {
      kind: 'catalog',
      catalogId: input.equipmentBasis.catalogId,
      manufacturer: input.equipmentBasis.manufacturer,
      model: input.equipmentBasis.model,
    };
  }
  const entryId = input.equipmentWorkspace?.appliedUserEquipmentId;
  if (entryId == null) return null;
  const entry = input.equipmentWorkspace?.userEquipment
    .find(candidate => candidate.id === entryId);
  if (!entry || validateGenerationUserEquipmentEntry(entry).length > 0) return null;
  const expected = applyGenerationUserEquipmentEnvelope(input, entryId);
  return unitsMatchAppliedDuty(input.units, expected.units)
    ? { kind: 'user', entryId, manufacturer: entry.manufacturer, model: entry.model }
    : null;
}

/**
 * Resize the applied equipment fleet to `blockCount` physical blocks by
 * re-running the owning apply function. Throws when no equipment selection is
 * active — generic fleets resize through the planning rebuild path instead.
 */
/**
 * The ONE contingency-block bound. Previously the UI input said `max="50"`, the
 * workbench guard silently rejected `> 64`, and this library threw above 1,000
 * — three different answers to the same question, two of them invisible.
 */
export const MIN_GENERATION_BLOCK_COUNT = 1;
export const MAX_GENERATION_BLOCK_COUNT = 64;

export function resizeGenerationEquipmentFleet(
  input: GenerationImpactScenarioInput,
  blockCount: number,
): GenerationImpactScenarioInput {
  if (!Number.isInteger(blockCount)
    || blockCount < MIN_GENERATION_BLOCK_COUNT
    || blockCount > MAX_GENERATION_BLOCK_COUNT) {
    throw new RangeError(
      `Equipment fleets resize to an integer block count between ${MIN_GENERATION_BLOCK_COUNT} and ${MAX_GENERATION_BLOCK_COUNT}.`,
    );
  }
  const selection = activeGenerationEquipmentSelection(input);
  if (!selection) {
    throw new RangeError('No applied equipment selection is available to resize.');
  }
  const template = input.units[0];
  if (!template) throw new RangeError('The scenario carries no generation units to resize.');
  const seeded = structuredClone(input);
  // Only the physical block COUNT matters to the apply functions — they
  // rebuild every unit field from the envelope's own normalization.
  seeded.units = Array.from({ length: blockCount }, (_, index) => ({
    ...structuredClone(template),
    id: `resize-seed-${index + 1}`,
    contingencyGroupId: `resize-seed-${index + 1}`,
    count: 1,
  }));
  // Both apply paths clear the project dossier, because in the general case a
  // new envelope invalidates it. A BLOCK-COUNT resize is the one case where it
  // does not: the equipment binding is unchanged and only the derived block
  // quantity moves. Carry it across rather than discarding the user's evidence.
  //
  // This holds for a user-supplied basis too, now that such entries carry an
  // immutable applied-duty fingerprint — the dossier re-binds to the same entry
  // at the new count.
  const carried = input.projectDutyBasis;
  const resized = selection.kind === 'catalog'
    ? applyGenerationEquipmentEnvelope(seeded, selection.catalogId, { allowUnconfirmed: true })
    : applyGenerationUserEquipmentEnvelope(seeded, selection.entryId);
  if (!carried) return resized;
  return rescaleGenerationProjectDutyBasis(resized, carried, blockCount);
}
